/**
 * The "choose a plan" prompt for accounts whose plan has lapsed (backend:
 * services/pricing/accessLock.js, GET /api/subscription/access).
 *
 * - Trial ended without paying, or a paid plan ended: a prompt over every
 *   page that can't be closed — they can still sign in, pick a plan
 *   (Settings → Subscription stays open) or sign out.
 * - A paying account's payment failed: a warning bar for the 14-day grace
 *   period, then the same prompt.
 *
 * Checked when the app opens and every 10 minutes after. An admin
 * impersonating the account can close it.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';

type Access = {
  locked: boolean;
  reason?: 'trial_ended' | 'plan_ended' | 'payment_failed';
  warning?: 'payment_failed';
  graceUntil?: string;
  planName?: string | null;
  isHolder?: boolean;
  canDismiss?: boolean;
};

const OPEN_PATHS = ['/settings', '/pricing', '/subscription', '/checkout'];

const AccessGate: React.FC = () => {
  const { token, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [access, setAccess] = useState<Access | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);

  const check = useCallback(() => {
    if (!token) return;
    fetch('/api/subscription/access', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setAccess(d); })
      .catch(() => { /* never lock on a network error */ });
  }, [token]);
  useEffect(() => {
    check();
    const t = setInterval(check, 10 * 60 * 1000);
    return () => clearInterval(t);
  }, [check]);

  const openBilling = async () => {
    setBusy(true);
    try {
      const r = await fetch('/api/stripe/create-portal-session', { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.url) { window.location.href = d.url; return; }
    } catch { /* fall back to Settings */ }
    setBusy(false);
    navigate('/settings?tab=subscription');
  };

  if (!access) return null;
  const onOpenPage = OPEN_PATHS.some((p) => location.pathname.startsWith(p));
  const graceDate = access.graceUntil ? new Date(access.graceUntil).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' }) : null;

  // Payment failed, still inside the grace period: a bar, not a block.
  if (!access.locked && access.warning === 'payment_failed') {
    return (
      <div className="fixed bottom-0 inset-x-0 z-[80] bg-amber-50 border-t border-amber-300 px-4 py-2.5 text-sm text-amber-900 flex items-center justify-center gap-3 flex-wrap"
        style={{ paddingBottom: 'calc(0.625rem + env(safe-area-inset-bottom, 0px))' }}>
        <span>
          <strong>Your last payment didn't go through.</strong> Update your payment details by {graceDate} to keep using MarketMapr.
        </span>
        {access.isHolder ? (
          <button onClick={openBilling} disabled={busy} className="px-3 py-1 rounded-md bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 disabled:opacity-60">
            {busy ? 'Opening…' : 'Update payment details'}
          </button>
        ) : <span className="text-xs">Please let your account holder know.</span>}
      </div>
    );
  }

  if (!access.locked || dismissed || onOpenPage) return null;

  const copy = {
    trial_ended: {
      title: 'Your free trial has ended',
      body: 'Choose a plan to carry on. Your projects, maps and everything you built during the trial are kept and will be waiting for you.',
    },
    plan_ended: {
      title: `Your ${access.planName ? `${access.planName} ` : ''}plan has ended`,
      body: 'Choose a plan to pick up where you left off. Your projects and data are kept and will be waiting for you.',
    },
    payment_failed: {
      title: "We couldn't take your last payment",
      body: 'Your account is paused until the payment goes through. Update your payment details to carry on; your projects and data are kept.',
    },
  }[access.reason || 'plan_ended'];

  return (
    <div className="fixed inset-0 z-[90] bg-gray-900/50 backdrop-blur-sm flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="access-gate-title">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <h2 id="access-gate-title" className="text-xl font-bold text-gray-900">{copy.title}</h2>
        <p className="text-sm text-gray-600 mt-2 leading-relaxed">{copy.body}</p>
        {!access.isHolder && (
          <p className="text-xs text-gray-500 mt-3">Your team's plan is managed by its account holder. Ask them to renew it, or choose a plan yourself.</p>
        )}
        <div className="mt-5 flex flex-col gap-2">
          {access.reason === 'payment_failed' && access.isHolder ? (
            <button onClick={openBilling} disabled={busy} className="w-full px-4 py-2.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-60">
              {busy ? 'Opening…' : 'Update payment details'}
            </button>
          ) : (
            <button onClick={() => navigate('/settings?tab=subscription')} className="w-full px-4 py-2.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700">
              Choose a plan
            </button>
          )}
          <div className="flex items-center justify-between text-sm mt-1">
            <span className="text-gray-500">Questions? alex@marketmapr.com</span>
            <button onClick={logout} className="text-gray-500 hover:text-gray-800 underline">Sign out</button>
          </div>
          {access.canDismiss && (
            <button onClick={() => setDismissed(true)} className="text-xs text-gray-400 hover:text-gray-700 mt-1">Close (you're viewing as an admin)</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccessGate;
